import _ from 'lodash';

import {
  buildEntityToken,
  ENTITY_REFERENCE_TYPE_ORDER,
  getEntityReferenceKey,
  getEntityReferenceTokens,
  parseEntityTokens,
  replaceEntityRangeWithText,
  sanitizeEntityLabel,
} from './TeamChat.entityReferences.utils';

// The composer shows references as a readable `#<label>` alias instead of the raw `[[type:id|label]]`
// wire token (DEV-5657). Aliases convert back to wire tokens only at SEND time — the POSTed body,
// badges, rail, and index all keep the token contract unchanged.
const ENTITY_ALIAS_PREFIX = '#';

export const buildEntityAlias = label => `${ENTITY_ALIAS_PREFIX}${sanitizeEntityLabel(label)}`;

// Replaces a `#query` range (or a soft-linkify span) with the entity's readable alias + a trailing
// space — the composer-side counterpart to the wire-token insertion the picker used before DEV-5657.
export const replaceEntityQueryWithAlias = ({ text, entityRange, entity }) => {
  if (!entityRange || !entity) {
    return { text, caret: text?.length ?? 0 };
  }

  return replaceEntityRangeWithText({
    text,
    entityRange,
    insertion: `${buildEntityAlias(entity.label)} `,
  });
};

// A nil ref marks a label the composer recorded as AMBIGUOUS (two different entities inserted
// under the same label) — it is excluded here so the alias degrades to plain text on send.
const getAliasMapEntries = aliasMap =>
  Object.entries(aliasMap ?? {})
    .filter(([, ref]) => !_.isNil(ref))
    .map(([label, ref]) => ({
      label: sanitizeEntityLabel(label),
      token: buildEntityToken({ entityType: ref.entityType, entityId: ref.entityId, label }),
    }))
    .filter(entry => entry.label.length > 0);

// Only labels owned by exactly ONE candidate are eligible for the candidate fallback — a label
// shared by two candidates is ambiguous and must be left as plain text (mirrors the soft-linkify
// exact-match discipline). Labels the composer already recorded as ambiguous (nil aliasMap ref)
// are excluded here too — otherwise a lone same-label candidate would silently re-resolve them.
// The aliasMap hint always wins for an inserted reference; this fallback only recovers references
// in a draft whose alias map was lost (e.g. a reloaded draft).
const getUniqueCandidateEntries = ({ candidatesByType, aliasMap }) => {
  const ambiguousLabels = new Set(
    Object.entries(aliasMap ?? {})
      .filter(([, ref]) => _.isNil(ref))
      .map(([label]) => sanitizeEntityLabel(label).toLowerCase()),
  );
  const candidates = ENTITY_REFERENCE_TYPE_ORDER.flatMap(type => candidatesByType?.[type] ?? []);
  const byLowerLabel = _.groupBy(candidates, candidate =>
    sanitizeEntityLabel(candidate.label).toLowerCase(),
  );

  return Object.values(byLowerLabel)
    .filter(group => group.length === 1)
    .map(([candidate]) => ({
      label: sanitizeEntityLabel(candidate.label),
      token: buildEntityToken(candidate),
    }))
    .filter(entry => entry.label.length > 0 && !ambiguousLabels.has(entry.label.toLowerCase()));
};

// Labels carry regex-hostile characters (`$ , . ( ) &`), so every label is escaped before it enters
// the alternation. Longest-first ordering prevents a shorter label from shadowing a longer one that
// starts with it. `caseInsensitive` matches the aliasMap-literal vs candidate-exact resolution split.
// Both paths require the same trailing boundary (no word char) so a label that is a prefix of
// adjacent typed text (`#W2xyz`) never converts mid-word, while trailing punctuation (`#W2,`) still
// resolves.
const buildAliasReplacer = ({ entries, caseInsensitive }) => {
  if (!entries.length) {
    return text => text;
  }

  const alternation = entries
    .map(entry => entry.label)
    .sort((first, second) => second.length - first.length)
    .map(_.escapeRegExp)
    .join('|');
  const pattern = new RegExp(
    `(?<=^|\\s)${ENTITY_ALIAS_PREFIX}(${alternation})(?!\\w)`,
    caseInsensitive ? 'gi' : 'g',
  );

  const tokenByLabel = new Map(
    entries.map(entry => [caseInsensitive ? entry.label.toLowerCase() : entry.label, entry.token]),
  );

  return text =>
    text.replace(pattern, (whole, label) => {
      const token = tokenByLabel.get(caseInsensitive ? label.toLowerCase() : label);

      return token ?? whole;
    });
};

/**
 * Converts every `#<label>` alias in a draft back to its `[[type:id|label]]` wire token, resolving
 * from `aliasMap` first (the exact reference the user inserted) then from a unique candidate-label
 * match. Aliases that resolve to nothing or are ambiguous are left as plain text. Raw wire tokens
 * already present in the draft (old drafts, pasted URLs) are preserved byte-for-byte — conversion
 * only touches the plain-text segments between them.
 */
export const convertAliasesToTokens = ({ text, aliasMap, candidatesByType }) => {
  if (!text) {
    return text ?? '';
  }

  const replaceFromAliasMap = buildAliasReplacer({
    entries: getAliasMapEntries(aliasMap),
    caseInsensitive: false,
  });
  const replaceFromCandidates = buildAliasReplacer({
    entries: getUniqueCandidateEntries({ candidatesByType, aliasMap }),
    caseInsensitive: true,
  });

  return parseEntityTokens(text)
    .map(segment =>
      segment.type === 'entityRef'
        ? segment.raw
        : replaceFromCandidates(replaceFromAliasMap(segment.value)),
    )
    .join('');
};

// Powers the composer preview strip: the resolved references for a draft holding wire tokens AND/OR
// aliases. An edited/no-longer-resolvable alias stays plain text (never a token) and drops out.
// De-duped per entity — a draft referencing the same entity twice previews as ONE chip (two
// identical chips read as a render bug, not an intentional restatement).
export const getDraftEntityReferences = ({ text, aliasMap, candidatesByType }) =>
  _.uniqBy(
    getEntityReferenceTokens(convertAliasesToTokens({ text, aliasMap, candidatesByType })),
    getEntityReferenceKey,
  ).map(({ entityType, entityId, label, key }) => ({ entityType, entityId, label, key }));
